import { createLogger, LogLevel } from "@figurepos/fgr-lib-backend/lib/logging/logger"
import { Maybe } from "@figurepos/fgr-lib-business-logic/lib/common/types"

type LoggerCtx = {
    accountId?: Maybe<string>
    userId?: Maybe<string>
    role?: Maybe<string>
}

const isTest = process.env.NODE_ENV === "test"
const isDevelopment = process.env.NODE_ENV === "development"

// Initialize Pino logger for server-side logging
// Note: OpenTelemetry Pino instrumentation automatically injects trace_id and span_id
const pinoLogger = createLogger({
    service: process.env.SERVICE_NAME as string,
    mode: isDevelopment ? "text" : "json",
    containerId: process.env.CONTAINER_ID ?? null,
    level: isDevelopment ? LogLevel.DEBUG : LogLevel.INFO,
    transformConfig: {
        "[figure-api-key]": "[figure-api-key]",
        "[twbrds-session]": "[twbrds-session]",
        "[token]": "[token]",
    },
})

export const Logger = {
    log: (level: string, message: string, data?: Maybe<object>, attributes?: Maybe<LoggerCtx>) => {
        // Browser logging in development
        if (isDevelopment) {
            // eslint-disable-next-line no-console
            console.log(message, data, attributes)
            return
        }

        // Browser logging - send to server endpoint
        if (typeof window !== "undefined" && !isTest) {
            const url = new URL(window.location.href)
            fetch(`${url.origin}/log`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    message: message,
                    data: data,
                    level: level,
                    attributes: attributes,
                }),
            }).catch(err => {
                // eslint-disable-next-line no-console
                console.error(err)
            })
            return
        }

        // Server-side logging using Pino
        // OpenTelemetry automatically injects trace_id and span_id
        const logData = {
            ...attributes,
            ...(typeof data === "object" && data !== null ? data : { data: data }),
        }

        switch (level.toLowerCase()) {
            case "error":
                pinoLogger.error(message, logData)
                break
            case "warn":
                pinoLogger.warn(message, logData)
                break
            case "info":
                pinoLogger.info(message, logData)
                break
            case "debug":
                pinoLogger.debug(message, logData)
                break
            default:
                pinoLogger.info(message, logData)
        }
    },

    info: (message: string, messageContext?: Maybe<object>, ctx?: LoggerCtx) => {
        Logger.log("info", message, messageContext, ctx)
    },

    warn: (message: string, messageContext?: Maybe<object>, error?: Error, ctx?: LoggerCtx) => {
        Logger.log(
            "warn",
            message,
            {
                error: error ?? undefined,
                data: messageContext ?? undefined,
            },
            ctx
        )
    },

    error: (message: string, messageContext?: Maybe<object>, error?: Error, ctx?: LoggerCtx) => {
        Logger.log(
            "error",
            message,
            {
                error: error ?? undefined,
                data: messageContext ?? undefined,
            },
            ctx
        )
    },
}
